import axios from 'axios';
import UploadAdapter, { progressEventType } from '../UploadAdapter';
import api from '@/service/api';
import uploadRequest from '../requestQueue';

interface S3Ticket {
    data: { url: string; fields: Record<string, string> };
}
const thumbTypes = ['small-thumb', 'large-thumb', 'no-watermark'] as const;

export const awsS3UploadService: UploadAdapter = {
    name: 'aws_s3',
    async uploadFile(ticket, image, progressCallback) {
        const controller = new AbortController();
        const cancel = () => controller.abort();
        const blobs = [image.file, image.thumbnails.smallThumb, image.thumbnails.largeThumb, image.thumbnails.noWatermark];
        const uploaded = blobs.map(() => 0);
        const overall = blobs.reduce((sum, blob) => sum + blob.size, 0);
        const progress = (event: progressEventType) => {
            if (!controller.signal.aborted) progressCallback(uploaded.reduce((sum, bytes) => sum + bytes, 0), overall, event);
        };
        const update = (index: number, loaded: number, total: number) => {
            // Multipart overhead varies; scale actual request progress to the payload size.
            uploaded[index] = Math.min(blobs[index].size, total > 0 ? loaded / total * blobs[index].size : loaded);
            progress('progress');
        };
        image.signal.addEventListener('abort', cancel, { once: true });
        if (image.signal.aborted) controller.abort();
        const requests: Promise<unknown>[] = [];
        try {
            progress('begin');
            const { url, fields } = (ticket as S3Ticket).data;
            const data = new FormData();
            Object.entries(fields).forEach(([name, value]) => data.append(name, value));
            data.append('file', image.file, image.id);
            requests.push(uploadRequest(() => axios.post(url, data, {
                signal: controller.signal,
                onUploadProgress: event => update(0, event.loaded, event.total || 0),
            }), controller.signal));
            for (const [index, type] of thumbTypes.entries()) {
                requests.push(uploadRequest(() => api.uploadFile(ticket.sessionId, image.id, type, blobs[index + 1],
                    (loaded, total) => update(index + 1, loaded, total), controller.signal,
                ), controller.signal));
            }
            const [, smallThumbUrl, largeThumbUrl] = await Promise.all(requests);
            if (controller.signal.aborted) return { cancelled: true };
            blobs.forEach((blob, index) => uploaded[index] = blob.size);
            progress('finish');
            return { cancelled: false, smallThumbUrl: smallThumbUrl as string, largeThumbUrl: largeThumbUrl as string };
        } catch (error) {
            controller.abort();
            await Promise.allSettled(requests);
            // Existing keys belong to the previous photo too. Never delete them on failed overwrite.
            // ponytail: atomic overwrite needs versioned object keys and backend commit support.
            if (!image.overwrite) {
                await Promise.all(['original' as const, ...thumbTypes].map(type =>
                    api.deleteFile(ticket.sessionId, image.id, type).catch(cleanupError => {
                        console.error(`Failed to clean up ${type} for ${image.id}`, cleanupError);
                    }),
                ));
            }
            if (image.signal.aborted) return { cancelled: true };
            throw error;
        } finally {
            image.signal.removeEventListener('abort', cancel);
        }
    },
};
